import { useEffect, useState } from "react";
import { Loader2, Wallet, AlertTriangle, Power } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, OPERATORS, STATUS, slotState, rupiah, ago } from "@/lib/api";
import { useAuth } from "@/context/Auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export function SlotDialog({ device, port, slot, data, low, onClose, onSaved }) {
  const { can } = useAuth();
  const isAdmin = can("slot_edit");
  const [name, setName] = useState("");
  const [operator, setOperator] = useState("");
  const [company, setCompany] = useState("");
  const [msisdn, setMsisdn] = useState("");
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [s, setS] = useState(data);

  useEffect(() => {
    setS(data);
    setName(data?.name || "");
    setOperator(data?.operator || "");
    setCompany(data?.company || "");
    setMsisdn(data?.msisdn || "");
  }, [data, port, slot]);

  if (!port) return null;
  const st = STATUS[slotState(s, low)];

  const save = async (clear) => {
    setBusy(true);
    try {
      const r = await api.put(`/slots/${device.id}/${port}/${slot}`, { name: clear ? "" : name, operator, company, number: clear ? undefined : msisdn });
      setS(r.data);
      toast.success(clear ? "Nama dikosongkan" : `Slot ${port}.${slot} → ${r.data.name}`);
      onSaved?.();
      if (clear) setName("");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const activate = async () => {
    if (!window.confirm(`Aktifkan ${s?.name || `slot ${port}.${slot}`} di port ${port}? SIM lain di port ini berhenti menerima SMS.`)) return;
    setSwitching(true);
    try {
      await api.post(`/devices/${device.id}/switch`, { changes: [{ port, slot }] });
      toast.success(`Perintah dikirim. Tunggu titik hijau (Register OK) sebelum ${s?.name || `${port}.${slot}`} dianggap ON.`);
      onSaved?.();
      setTimeout(() => onSaved?.(), 10000);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSwitching(false);
    }
  };

  const check = async () => {
    setChecking(true);
    try {
      const r = await api.post(`/slots/${device.id}/${port}/${slot}/check-balance`);
      setS((o) => ({ ...o, balance: r.data.balance ?? o?.balance, balance_raw: r.data.reply, balance_at: r.data.created_at }));
      toast.success(r.data.balance != null ? `Pulsa ${rupiah(r.data.balance)}` : "Balasan diterima (saldo tidak terbaca)");
      onSaved?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setChecking(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="slot-dialog" className="bg-white border-slate-300 max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-baseline gap-3">
            <span className="font-display text-3xl text-emerald-700">{s?.name || "Tanpa nama"}</span>
            <span className="font-mono text-sm text-slate-500">{device.name} · {port}.{slot}</span>
          </DialogTitle>
        </DialogHeader>
        {s?.sim_changed && (
          <div data-testid="slot-sim-changed-warning" className="flex gap-2 text-sm rounded border border-amber-300 bg-amber-50 text-amber-800 p-3">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> SIM di slot ini terlihat berganti. Periksa dan perbarui namanya.
          </div>
        )}
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Info label="Status"><span className={`inline-flex items-center gap-1.5 ${st.text}`}><span className={`h-2 w-2 rounded-full ${st.dot}`} />{st.label}</span></Info>
          <Info label="Operator">{OPERATORS[s?.operator]?.label || s?.opr_raw || "—"}</Info>
          <Info label="Nomor"><span data-testid="slot-msisdn">{s?.msisdn || s?.number || "—"}</span></Info>
          <Info label="ICCID"><span className="font-mono text-xs break-all">{s?.iccid || "—"}</span></Info>
          <Info label="Pulsa terakhir"><span data-testid="slot-balance" className="font-mono font-bold">{rupiah(s?.balance)}</span></Info>
          <Info label="Dicek">{ago(s?.balance_at)}</Info>
          <Info label="PT"><span data-testid="slot-company">{s?.company || "—"}</span></Info>
        </div>
        {s?.balance_raw && <pre data-testid="slot-balance-raw" className="text-xs font-mono whitespace-pre-wrap bg-slate-50 border border-slate-200 rounded p-3 text-emerald-800">{s.balance_raw}</pre>}
        {can("check_balance") && <Button data-testid="check-balance-button" onClick={check} disabled={checking} className="bg-emerald-500 hover:bg-emerald-600 text-white">
          {checking ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Wallet className="h-4 w-4 mr-2" />} Cek pulsa sekarang
        </Button>}
        {!s?.current && can("switch_sim") && (
          <Button data-testid="slot-activate-button" variant="outline" onClick={activate} disabled={switching}>
            {switching ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Power className="h-4 w-4 mr-2" />} Aktifkan SIM ini di port {port}
          </Button>
        )}
        {isAdmin && (
          <div className="space-y-2 border-t border-slate-200 pt-4">
            <label className="text-xs uppercase tracking-wider text-slate-500">Nama slot, operator & PT</label>
            <div className="flex gap-2">
              <Input data-testid="slot-name-input" placeholder="mis. tsel10" value={name} onChange={(e) => setName(e.target.value)} className="bg-white" />
              <Select value={operator || "none"} onValueChange={(v) => setOperator(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="slot-operator-select" className="w-36 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Otomatis</SelectItem>
                  {Object.entries(OPERATORS).map(([k, o]) => <SelectItem key={k} value={k}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Input data-testid="slot-msisdn-input" placeholder="Nomor SIM (opsional), mis. 081216132149" value={msisdn} onChange={(e) => setMsisdn(e.target.value)} className="bg-white font-mono" />
            <Input data-testid="slot-company-input" placeholder="Nama PT (opsional), mis. BP" value={company} onChange={(e) => setCompany(e.target.value)} className="bg-white" />
            <div className="flex gap-2">
              <Button data-testid="slot-rename-save" disabled={busy} onClick={() => save(false)} className="flex-1">Simpan</Button>
              <Button data-testid="slot-rename-clear" disabled={busy} variant="outline" onClick={() => save(true)}>Kosongkan nama</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Info({ label, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}
